import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';

function Neo4jEditor() {
  const { state, dispatch } = useApp();
  const [command, setCommand] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [missions, setMissions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteType, setAutocompleteType] = useState(''); // '@' or '$'
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [autocompletePosition, setAutocompletePosition] = useState(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'system',
      content: '🛠️ Welcome to your Mission Editor Agent! Use natural language commands to modify your database. You can create missions, assign team members, manage dependencies, and more. Use @username to mention users and $mission to reference missions.',
      timestamp: new Date().toLocaleTimeString(),
      isWelcome: true
    }
  ]);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Fetch missions when component mounts
  useEffect(() => {
    const fetchMissions = async () => {
      if (!state.driver) return;
      
      try {
        const session = state.driver.session();
        const result = await session.run('MATCH (m:MISSION) RETURN m.id as id, m.title as title, m.state as state ORDER BY m.title');
        
        const missionData = result.records.map(record => ({
          id: record.get('id'),
          title: record.get('title'),
          state: record.get('state')
        }));
        
        setMissions(missionData);
        await session.close();
      } catch (error) {
        console.error('Error fetching missions:', error);
      }
    };

    fetchMissions();
  }, [state.driver]);

  // Handle input changes and autocomplete logic
  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    setCommand(value);
    
    // Check for autocomplete triggers
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const lastDollarIndex = textBeforeCursor.lastIndexOf('$');
    
    let triggerIndex = -1;
    let triggerType = '';
    
    if (lastAtIndex > lastDollarIndex && lastAtIndex !== -1) {
      triggerIndex = lastAtIndex;
      triggerType = '@';
    } else if (lastDollarIndex > lastAtIndex && lastDollarIndex !== -1) {
      triggerIndex = lastDollarIndex;
      triggerType = '$';
    }
    
    if (triggerIndex !== -1) {
      const textAfterTrigger = textBeforeCursor.substring(triggerIndex + 1);
      const hasSpaceAfterTrigger = textAfterTrigger.includes(' ');
      
      if (!hasSpaceAfterTrigger) {
        setShowAutocomplete(true);
        setAutocompleteType(triggerType);
        setAutocompleteQuery(textAfterTrigger);
        setAutocompletePosition(triggerIndex);
        setSelectedSuggestionIndex(0);
      } else {
        setShowAutocomplete(false);
      }
    } else {
      setShowAutocomplete(false);
    }
  };

  // Get filtered suggestions based on autocomplete type and query
  const getSuggestions = () => {
    if (autocompleteType === '@') {
      const users = Object.keys(state.userProfiles || {});
      return users.filter(user => 
        user.toLowerCase().includes(autocompleteQuery.toLowerCase())
      ).slice(0, 5);
    } else if (autocompleteType === '$') {
      return missions.filter(mission => 
        mission.title.toLowerCase().includes(autocompleteQuery.toLowerCase()) ||
        mission.id.toLowerCase().includes(autocompleteQuery.toLowerCase())
      ).slice(0, 5);
    }
    return [];
  };

  // Handle suggestion selection
  const selectSuggestion = (suggestion) => {
    const beforeTrigger = command.substring(0, autocompletePosition);
    const afterQuery = command.substring(autocompletePosition + 1 + autocompleteQuery.length);
    
    let selectedText = '';
    if (autocompleteType === '@') {
      selectedText = suggestion;
    } else if (autocompleteType === '$') {
      selectedText = suggestion.title;
    }
    
    const newCommand = beforeTrigger + autocompleteType + selectedText + ' ' + afterQuery;
    setCommand(newCommand);
    setShowAutocomplete(false);
    
    // Focus back to input
    setTimeout(() => {
      inputRef.current?.focus();
      const newCursorPosition = beforeTrigger.length + selectedText.length + 2;
      inputRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  // Handle keyboard navigation in autocomplete
  const handleKeyDown = (e) => {
    if (!showAutocomplete) return;
    
    const suggestions = getSuggestions();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => 
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => 
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions[selectedSuggestionIndex]) {
        selectSuggestion(suggestions[selectedSuggestionIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const executeCommand = async (previewOnly = false) => {
    if (!command.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Store the command before clearing it
    const currentCommand = command;
    
    // Add user message to chat history
    const userMessage = {
      type: 'user',
      content: currentCommand,
      timestamp: new Date().toLocaleTimeString(),
      isPreview: previewOnly
    };
    
    // Add loading message
    const loadingMessage = {
      type: 'ai',
      content: previewOnly ? 'Previewing command...' : 'Executing command...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setChatHistory(prev => [...prev, userMessage, loadingMessage]);
    setCommand(''); // Clear input immediately

    try {
      // Neo4j Editor API request
      const requestBody = {
        command: currentCommand,
        user_id: "Rahul",
        confirm_execution: !previewOnly,
        auto_sync_cache: !previewOnly
      };

      // Make the actual API call to Neo4j Editor system
      const response = await fetch('https://langraphdev-development.up.railway.app/api/hey-mission/edit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(requestBody)
      });

      // Get response as text first to see what we're getting
      const responseText = await response.text();

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        // Check if it's an HTML error page
        if (responseText.includes('<!DOCTYPE') || responseText.includes('<html>')) {
          responseData = { error: 'Server returned HTML instead of JSON - likely a CORS or server error' };
        } else {
          responseData = { 
            error: 'Invalid response format', 
            rawResponse: responseText.substring(0, 500) + '...' 
          };
        }
      }

      if (response.ok && responseData && responseData.success) {
        // Extract response information
        const message = responseData.message || 'Command processed successfully';
        const commandAnalysis = responseData.command_analysis;
        const cypherGeneration = responseData.cypher_generation;
        const executionResult = responseData.execution_result;
        const latency = responseData.total_latency_ms;

        // Remove loading message and add AI response
        setChatHistory(prev => {
          const newHistory = prev.filter(msg => !msg.isLoading);
          
          // Create AI response message
          const editorResponse = {
            type: 'ai',
            content: message,
            timestamp: new Date().toLocaleTimeString(),
            isSuccess: true,
            isPreview: previewOnly,
            metadata: {
              commandAnalysis,
              cypherGeneration,
              executionResult: previewOnly ? null : executionResult,
              latency: latency ? Math.round(latency) : null,
              operationType: commandAnalysis?.operation_type,
              confidence: commandAnalysis?.confidence
            }
          };
          
          return [...newHistory, editorResponse];
        });
      } else {
        const errorMsg = responseData?.error || responseData?.message || `HTTP ${response.status}: ${response.statusText}`;
        
        // Remove loading message and add error response
        setChatHistory(prev => {
          const newHistory = prev.filter(msg => !msg.isLoading);
          
          const errorResponse = {
            type: 'ai',
            content: `❌ Command ${previewOnly ? 'preview' : 'execution'} failed: ${errorMsg}`,
            timestamp: new Date().toLocaleTimeString(),
            isError: true
          };
          
          return [...newHistory, errorResponse];
        });
      }

    } catch (error) {
      // Remove loading message and add error response
      setChatHistory(prev => {
        const newHistory = prev.filter(msg => !msg.isLoading);
        
        const errorResponse = {
          type: 'ai',
          content: `❌ Network error: ${error.message}`,
          timestamp: new Date().toLocaleTimeString(),
          isError: true
        };
        
        return [...newHistory, errorResponse];
      });
    }

    setIsSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't submit if autocomplete is showing
    if (showAutocomplete) {
      const suggestions = getSuggestions();
      if (suggestions[selectedSuggestionIndex]) {
        selectSuggestion(suggestions[selectedSuggestionIndex]);
      }
      return;
    }
    
    await executeCommand(isPreviewMode);
  };

  const goBackToDashboard = () => {
    dispatch({ type: ActionTypes.SET_SCREEN, payload: 'dashboard' });
  };

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-4xl mx-auto h-[80vh] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-white">Mission Editor Agent</h1>
        <button 
          onClick={goBackToDashboard}
          className="text-sm text-gray-400 hover:text-white"
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Mode Toggle */}
      <div className="mb-4 p-3 bg-gray-900/30 rounded-lg border border-gray-700">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-400">Execution Mode:</span>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="mode"
              checked={!isPreviewMode}
              onChange={() => setIsPreviewMode(false)}
              className="text-gray-500 focus:ring-gray-500"
            />
            <span className="text-sm text-white">Execute</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="mode"
              checked={isPreviewMode}
              onChange={() => setIsPreviewMode(true)}
              className="text-gray-500 focus:ring-gray-500"
            />
            <span className="text-sm text-white">Preview Only</span>
          </label>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          {isPreviewMode ? '🔍 Preview mode: Generate Cypher without executing' : '⚡ Execute mode: Apply changes to database'}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-gray-900/50 rounded-lg p-4 overflow-y-auto mb-4 space-y-3">
        {chatHistory.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-3 ${
              message.type === 'user' 
                ? message.isPreview 
                  ? 'bg-gray-600 text-white border border-gray-500' 
                  : 'bg-gray-700 text-white border border-gray-600'
                : message.isError
                ? 'bg-red-900/50 text-red-200 border border-red-500'
                : message.isSuccess
                ? message.isPreview
                  ? 'bg-gray-600 text-white border border-gray-500'
                  : 'bg-gray-600 text-white border border-gray-500'
                : message.isWelcome
                ? 'bg-black text-gray-200 border border-gray-600'
                : 'bg-gray-700 text-gray-200 border border-gray-600'
            }`}>
              <div className="text-sm">
                {message.isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner w-4 h-4"></div>
                    {message.content}
                  </div>
                ) : (
                  <>
                    {message.content}
                    
                    {/* Show metadata for successful AI responses */}
                    {message.metadata && (
                      <div className="mt-3 pt-3 border-t border-gray-600/50 space-y-2">
                        {/* Command Analysis */}
                        {message.metadata.commandAnalysis && (
                          <div className="bg-gray-800/50 p-3 rounded border border-gray-600/30">
                            <div className="text-xs font-semibold text-gray-300 mb-2">📊 Command Analysis</div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                              <div><strong>Operation:</strong> {message.metadata.commandAnalysis.operation_type}</div>
                              <div><strong>Confidence:</strong> {Math.round(message.metadata.commandAnalysis.confidence * 100)}%</div>
                            </div>
                            {message.metadata.commandAnalysis.reasoning && (
                              <div className="mt-2 text-xs text-gray-300">
                                <strong>Reasoning:</strong> {message.metadata.commandAnalysis.reasoning}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Cypher Generation */}
                        {message.metadata.cypherGeneration && (
                          <div className="bg-gray-800/50 p-3 rounded border border-gray-600/30">
                            <div className="text-xs font-semibold text-gray-300 mb-2">🔧 Generated Cypher</div>
                            <div className="bg-black/30 p-2 rounded text-xs font-mono text-green-300 overflow-x-auto">
                              {message.metadata.cypherGeneration.cypher_query}
                            </div>
                            <div className="mt-2 text-xs text-gray-400">
                              <strong>Summary:</strong> {message.metadata.cypherGeneration.operation_summary}
                            </div>
                          </div>
                        )}
                        
                        {/* Execution Results */}
                        {message.metadata.executionResult && (
                          <div className="bg-green-900/20 p-3 rounded border border-green-500/30">
                            <div className="text-xs font-semibold text-green-300 mb-2">✅ Execution Results</div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                              <div><strong>Records Created:</strong> {message.metadata.executionResult.records_created}</div>
                              <div><strong>Properties Set:</strong> {message.metadata.executionResult.properties_set}</div>
                              <div><strong>Successful:</strong> {message.metadata.executionResult.successful_statements}</div>
                              <div><strong>Total:</strong> {message.metadata.executionResult.total_statements}</div>
                            </div>
                          </div>
                        )}
                        
                        {/* General Metadata */}
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                          {message.metadata.latency && (
                            <div><strong>Response Time:</strong> {message.metadata.latency}ms</div>
                          )}
                          <div><strong>Mode:</strong> {message.isPreview ? 'Preview' : 'Execute'}</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="text-xs opacity-70 mt-1">
                {message.timestamp}
                {message.isPreview && <span className="ml-2 text-yellow-300">👁️ Preview</span>}
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <div className="relative">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={command}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter natural language command (use @username for users, $mission for missions)"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-sm"
              disabled={isSubmitting}
            />
            
            {/* Autocomplete Dropdown */}
            {showAutocomplete && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                {getSuggestions().length > 0 ? (
                  <>
                    <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-600">
                      {autocompleteType === '@' ? '👤 Users' : '🎯 Missions'}
                    </div>
                    {getSuggestions().map((suggestion, index) => (
                      <div
                        key={autocompleteType === '@' ? suggestion : suggestion.id}
                        className={`px-3 py-2 cursor-pointer text-sm ${
                          index === selectedSuggestionIndex
                            ? 'bg-gray-600 text-white'
                            : 'text-gray-200 hover:bg-gray-700'
                        }`}
                        onClick={() => selectSuggestion(suggestion)}
                      >
                        {autocompleteType === '@' ? (
                          <div className="flex items-center gap-2">
                            <span className="text-blue-400">@</span>
                            <span>{suggestion}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="text-green-400">$</span>
                              <span className="font-medium">{suggestion.title}</span>
                              {suggestion.state && (
                                <span className="text-xs px-2 py-0.5 bg-gray-600 rounded text-gray-300">
                                  {suggestion.state}
                                </span>
                              )}
                            </div>
                            {suggestion.id && (
                              <div className="text-xs text-gray-400 ml-6">
                                ID: {suggestion.id}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    No {autocompleteType === '@' ? 'users' : 'missions'} found
                  </div>
                )}
              </div>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting || !command.trim()}
          className={`${
            isPreviewMode 
              ? 'bg-gray-600 hover:bg-gray-500 border border-gray-500' 
              : 'bg-gray-700 hover:bg-gray-600 border border-gray-600'
          } text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 flex items-center gap-2`}
          >
            {isSubmitting ? (
              <>
                <div className="spinner w-4 h-4"></div>
                {isPreviewMode ? 'Preview' : 'Execute'}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                {isPreviewMode ? 'Preview' : 'Execute'}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Example Commands */}
      <div className="mt-3 text-xs text-gray-500">
        <p className="mb-1">💡 <strong>Example commands with autocomplete:</strong></p>
        <div className="space-y-1 text-gray-400">
          <p>• "Create mission Audio System with description Implement 3D spatial audio"</p>
          <p>• "Assign <span className="text-blue-400">@Amaresh</span> to lead the <span className="text-green-400">$Audio System</span> mission"</p>
          <p>• "Update <span className="text-green-400">$Audio System</span> mission status to Active"</p>
          <p>• "Make <span className="text-green-400">$Audio System</span> depend on <span className="text-green-400">$Core Engine</span> mission"</p>
          <p>• "Add <span className="text-blue-400">@Rahul</span> to the Programming Team circle"</p>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          <span className="text-blue-400">@</span> = Users • <span className="text-green-400">$</span> = Missions • Use ↑↓ to navigate • Enter to select • Esc to close
        </div>
      </div>
    </div>
  );
}

export default Neo4jEditor;
